import axios from 'axios';
import { clearAuthCookies, getCookie, getRefreshTokenCookie, setCookie, setRefreshTokenCookie } from '../components/login/cookies';
import { constants } from './constants';

export const getAuthTokenFromLocalStorage = () => {
    return localStorage.getItem('authToken') || null;
};

const ongoingRequests = new Map();
let activeRefreshPromise: Promise<string | null> | null = null;

type RefreshErrorCode = 'REFRESH_INVALID' | 'REFRESH_EXPIRED' | 'REFRESH_REUSED' | 'REFRESH_REVOKED';

interface RefreshResponse {
    'authentication-token'?: string;
    'refresh-token'?: string;
    access_expires_in?: number;
    refresh_expires_in?: number;
}

interface RequestConfigWithRetry {
    _retry?: boolean;
    _skipAuthRefresh?: boolean;
}

const REFRESH_PATH = 'auth/token/refresh/';
const LOGIN_PATH = 'auth/company/user/login/other/';
const LOGOUT_PATH = 'auth/company/user/logout/other/';
const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 60;
const TERMINAL_REFRESH_ERROR_CODES: RefreshErrorCode[] = [
    'REFRESH_INVALID',
    'REFRESH_EXPIRED',
    'REFRESH_REUSED',
    'REFRESH_REVOKED',
];

const getRequestKey = (config: any) => `${config.method}:${config.url}`;

const buildApiUrl = (path: string): string => {
    const baseUrl = constants.API_BASE_URL.endsWith('/')
        ? constants.API_BASE_URL
        : `${constants.API_BASE_URL}/`;

    return new URL(path, baseUrl).toString();
};

const redirectToLogin = (): void => {
    if (window.location.pathname !== '/login') {
        window.location.assign('/login');
    }
};

const clearSessionAndRedirect = (): void => {
    clearAuthCookies();
    localStorage.removeItem('authenticated');
    redirectToLogin();
};

const reloadCurrentPage = (): void => {
    if (window.location.pathname !== '/login') {
        window.location.reload();
    }
};

const parseJwtExpiryEpochSeconds = (token: string): number | null => {
    try {
        const encodedPayload = token.split('.')[1];
        if (!encodedPayload) {
            return null;
        }

        const base64Payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
        const decodedPayload = atob(base64Payload);
        const payload = JSON.parse(decodedPayload) as { exp?: number };

        return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
        return null;
    }
};

const shouldRefreshBeforeExpiry = (token: string): boolean => {
    const expiryEpochSeconds = parseJwtExpiryEpochSeconds(token);
    if (!expiryEpochSeconds) {
        return false;
    }

    const currentEpochSeconds = Math.floor(Date.now() / 1000);
    return expiryEpochSeconds - currentEpochSeconds <= ACCESS_TOKEN_REFRESH_BUFFER_SECONDS;
};

const shouldSkipRefreshForUrl = (url?: string): boolean => {
    if (!url) {
        return false;
    }

    return url.includes(REFRESH_PATH) || url.includes(LOGOUT_PATH) || url.includes(LOGIN_PATH);
};

const refreshAccessToken = async (options?: { reloadOnSuccess?: boolean }): Promise<string | null> => {
    const refreshToken = getRefreshTokenCookie();

    if (!refreshToken) {
        clearSessionAndRedirect();
        return null;
    }

    try {
        const response = await api.post<RefreshResponse>(
            buildApiUrl(REFRESH_PATH),
            { refresh_token: refreshToken },
            { _skipAuthRefresh: true } as any
        );

        const newAccessToken = response.data?.['authentication-token'];
        const newRefreshToken = response.data?.['refresh-token'];

        if (!newAccessToken || !newRefreshToken) {
            clearSessionAndRedirect();
            return null;
        }

        setCookie('token', newAccessToken, 1);
        setRefreshTokenCookie(newRefreshToken, 14);

        if (options?.reloadOnSuccess) {
            reloadCurrentPage();
        }

        return newAccessToken;
    } catch (error: any) {
        const statusCode: number | undefined = error?.response?.status;
        const errorCode: RefreshErrorCode | undefined = error?.response?.data?.error_code;

        if (
            statusCode === 401 ||
            statusCode === 403 ||
            (errorCode !== undefined && TERMINAL_REFRESH_ERROR_CODES.includes(errorCode))
        ) {
            clearSessionAndRedirect();
        }

        return null;
    }
};

const api = axios.create({
    baseURL: constants.API_BASE_URL,
    timeout: 450000,
});
  

api.interceptors.request.use(
        async response => {
                const requestConfig = response as typeof response & RequestConfigWithRetry;
                if (requestConfig._skipAuthRefresh) {
                        return requestConfig;
                }

        let authToken = getCookie("token");
        if (authToken && shouldRefreshBeforeExpiry(authToken)) {
            if (!activeRefreshPromise) {
                activeRefreshPromise = refreshAccessToken().finally(() => {
                    activeRefreshPromise = null;
                });
            }

            const refreshedToken = await activeRefreshPromise;
            if (refreshedToken) {
                authToken = refreshedToken;
            }
        }

        if (authToken) {
                        response.headers = response.headers ?? {};
                        response.headers['Authorization'] = `Bearer ${authToken}`;
        }
        return response;
    }
);

api.interceptors.response.use(
    response => {
      return response;
    },
        async error => {
            const statusCode: number | undefined = error?.response?.status;
            const originalRequest = (error?.config ?? {}) as any;

            if (
                (statusCode !== 401 && statusCode !== 403) ||
                originalRequest._retry ||
                originalRequest._skipAuthRefresh ||
                shouldSkipRefreshForUrl(originalRequest.url)
            ) {
                return Promise.reject(error);
            }

            if (!activeRefreshPromise) {
                activeRefreshPromise = refreshAccessToken({ reloadOnSuccess: true }).finally(() => {
                    activeRefreshPromise = null;
                });
            }

            const refreshedAccessToken = await activeRefreshPromise;

            if (!refreshedAccessToken) {
                return Promise.reject(error);
      }

            originalRequest._retry = true;
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${refreshedAccessToken}`;

            return api(originalRequest);
    }
);

api.interceptors.request.use(
  (config) => {
    const requestKey = getRequestKey(config);

    if (ongoingRequests.has(requestKey)) {
      // console.warn(`Duplicate request blocked: ${requestKey}`);
      return Promise.reject({ isDuplicate: true }); // Reject duplicates
    }

    ongoingRequests.set(requestKey, config);

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    const requestKey = getRequestKey(response.config);
    ongoingRequests.delete(requestKey); // Remove completed request
    return response;
  },
  (error) => {
    if (error.config) {
      const requestKey = getRequestKey(error.config);
      ongoingRequests.delete(requestKey); // Remove failed request
    }
    return Promise.reject(error);
  }
);

export const headers = {
    'Cache-Control': 'no-cache',
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Authorization': 'Bearer ' + getCookie("token"),
    // 'ngrok-skip-browser-warning': 'true',
    // 'User-Agent': 'MyCustomAgent',
};

export const createRoute = async (requestData: any): Promise<any> => {
    try {
        const response = await api.post(
            '/route/',
            requestData,
            { headers: headers }
        );
        return response.data;
    } catch (error) {
        handleAxiosError(error);
    }
};

export const fetchConfigOptions = async (configId: string): Promise<any> => {
    try {
        const response = await api.get(
            `/project/common/config_options/?config_id=${configId}`,
            { headers: headers }
        );
        return response.data;
    } catch (error) {
        handleAxiosError(error);
    }
};

export const handleAxiosError = (error: any) => {
    if (axios.isAxiosError(error)) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Failed to process the request');
        } else if (error.request) {
            throw new Error('No response received from server');
        } else {
            throw new Error('Error setting up the request');
        }
    } else {
        throw new Error('An unexpected error occurred');
    }
};

export default api;
